import React, { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";

import { list, listOrgs } from "../../services/api/Enterprise";
import { EnterpriseTable } from "./components/EnterpriseTable";
import { OrganizationsTable } from "../Organizations/components/OrganizationsTable";
import MenuBar from "../../components/MenuBar";
import { DocumentSearch } from "../../components/DocumentSearch";
import { DocumentTabNav } from "../../components/DocumentTabNav";
import Loader from "../../common/Loader";
import { useApi } from "../../hooks/useApi";

export const EnterprisePage: React.FunctionComponent = () => {
  const [currentTab, setCurrentTab] = useState<"orgs" | "grids">("orgs");

  // State for Enterprise Grids search (NEW)
  const [gridsResult, setGridsSearch] = useApi(list);
  const [gridsSearchParams, setGridsSearchParams] = useState({
    search: "",
    showDeleted: false,
  });
  const [debouncedGridsSearch] = useDebounce(gridsSearchParams.search, 500);

  // State for Organizations search by Grid ID (OLD)
  const [orgsResult, setOrgsSearch] = useApi(listOrgs);
  const [orgsSearchParams, setOrgsSearchParams] = useState({
    search: "",
    showDeleted: false,
  });
  const [debouncedOrgsSearch] = useDebounce(orgsSearchParams.search, 500);

  // Load Enterprise Grids when search changes
  useEffect(() => {
    setGridsSearch({ search: debouncedGridsSearch });
  }, [debouncedGridsSearch, setGridsSearch]);

  // Load Organizations when search changes
  useEffect(() => {
    setOrgsSearch({ search: debouncedOrgsSearch });
  }, [debouncedOrgsSearch, setOrgsSearch]);

  const tabs = [
    {
      id: "orgs",
      label: "Orgs View",
      onClick: () => setCurrentTab("orgs"),
    },
    {
      id: "grids",
      label: "Grid View",
      onClick: () => setCurrentTab("grids"),
    },
  ];

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col p-0">
          <MenuBar />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="p-4">
            <h1>Enterprise</h1>
            <DocumentTabNav initialTab="orgs" tabs={tabs} />

            <div className="mt-4">
              {/* Organizations by Grid ID View (OLD - Default) */}
              {currentTab === "orgs" && (
                <>
                  <div className="card">
                    <div className="card-header">
                      <h2 className="card-title">Enterprises</h2>
                      <DocumentSearch
                        typeName="Enterprise"
                        placeholder="Enterprise Grid ID or Slack Enterprise ID (exact match)"
                        numFound={orgsResult.data?.length}
                        omitDeleted={true}
                        onSubmit={setOrgsSearchParams}
                        loading={orgsResult.isLoading}
                      />
                    </div>
                  </div>
                  <div className="card bg-dark mt-2">
                    {orgsResult.isLoading ? (
                      <Loader />
                    ) : (
                      <>
                        {orgsResult?.data && orgsResult.data.length > 0 && (
                          <OrganizationsTable organizations={orgsResult.data} />
                        )}
                        {orgsResult?.data &&
                          orgsResult.data.length === 0 &&
                          orgsSearchParams.search && (
                            <div className="alert alert-info m-3">
                              No organizations found for this Enterprise Grid ID
                            </div>
                          )}
                        {orgsResult?.error && (
                          <div className="alert alert-danger m-3">
                            {orgsResult.error.toString()}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              {/* Enterprise Grids View (NEW) */}
              {currentTab === "grids" && (
                <>
                  <div className="card">
                    <div className="card-header">
                      <h2 className="card-title">
                        Enterprise Grids{" "}
                        <span className="badge badge-success ml-2">New</span>
                      </h2>
                      <DocumentSearch
                        typeName="Enterprise Grid"
                        placeholder="Enterprise Grid Name or Slack Enterprise ID"
                        numFound={gridsResult.data?.length}
                        omitDeleted={true}
                        onSubmit={setGridsSearchParams}
                        loading={gridsResult.isLoading}
                      />
                    </div>
                  </div>
                  <div className="card bg-dark mt-2">
                    {gridsResult.isLoading ? (
                      <Loader />
                    ) : (
                      <>
                        {gridsResult?.data && (
                          <EnterpriseTable grids={gridsResult.data} />
                        )}
                        {gridsResult?.error && (
                          <div className="alert alert-danger">
                            {gridsResult.error.toString()}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
